import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Download, ShieldCheck, ShieldAlert, Loader2, FileText, Info } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const LIGHT_STYLES = {
  green: {
    bg: "bg-emerald-50 border-emerald-200",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    label: "Conforme",
  },
  yellow: {
    bg: "bg-amber-50 border-amber-200",
    text: "text-amber-700",
    dot: "bg-amber-500",
    label: "Parcial",
  },
  red: {
    bg: "bg-red-50 border-red-200",
    text: "text-red-700",
    dot: "bg-red-500",
    label: "Insuficiente",
  },
};

export function ComplianceCard({ restaurantId, reloadKey = 0 }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const data = await api.getAllergensComplianceSummary(restaurantId);
      setSummary(data);
    } catch {
      // Silent — failure here doesn't block the rest of the tab.
    }
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await api.downloadAllergensComplianceReport(restaurantId);
      toast.success("Informe de cumplimiento descargado", {
        description: "PDF firmado digitalmente para inspectores de sanidad.",
      });
    } catch (e) {
      toast.error(e?.message || "No se pudo generar el informe");
    }
    setDownloading(false);
  };

  if (loading || !summary) {
    return (
      <div
        className="rounded-xl border border-border bg-gradient-to-br from-slate-50 to-white px-5 py-4 flex items-center gap-3"
        data-testid="compliance-card-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        <span className="text-sm text-muted-foreground">Calculando cumplimiento EU 1169…</span>
      </div>
    );
  }

  const coverageStyle = LIGHT_STYLES[summary.coverage_light] || LIGHT_STYLES.red;
  const verifiedStyle = LIGHT_STYLES[summary.verified_light] || LIGHT_STYLES.red;
  const overall =
    summary.coverage_light === "green" && summary.verified_light === "green"
      ? "green"
      : summary.coverage_light === "red" || summary.verified_light === "red"
      ? "red"
      : "yellow";
  const overallStyle = LIGHT_STYLES[overall];

  return (
    <div
      className={`rounded-xl border ${overallStyle.bg} px-5 py-4 space-y-4`}
      data-testid="compliance-card"
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`p-2 rounded-lg bg-card shadow-sm`}>
            {overall === "green" ? (
              <ShieldCheck className={`w-5 h-5 ${overallStyle.text}`} />
            ) : (
              <ShieldAlert className={`w-5 h-5 ${overallStyle.text}`} />
            )}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground text-sm">
                Cumplimiento EU 1169/2011
              </h3>
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${overallStyle.text} bg-card border border-current/20`}
              >
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${overallStyle.dot} mr-1 align-middle`} />
                {overallStyle.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Descarga el informe firmado digitalmente para presentarlo en una
              inspección de sanidad. Incluye huella SHA-256 a prueba de
              manipulaciones.
            </p>
          </div>
        </div>

        <Button
          onClick={handleDownload}
          disabled={downloading || !summary.total_dishes}
          size="sm"
          className="gap-2 shrink-0"
          data-testid="download-compliance-pdf-btn"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Descargar informe PDF
        </Button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className={`rounded-lg bg-card/80 border ${coverageStyle.bg.replace("bg-", "border-").replace("-50", "-200")} px-3 py-2`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Cobertura
            </span>
            <span className={`inline-block w-2 h-2 rounded-full ${coverageStyle.dot}`} />
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className={`text-xl font-bold ${coverageStyle.text}`} data-testid="coverage-pct">
              {summary.coverage_pct}%
            </span>
            <span className="text-[11px] text-muted-foreground">
              ({summary.with_allergens}/{summary.total_dishes})
            </span>
          </div>
        </div>

        <div className={`rounded-lg bg-card/80 border ${verifiedStyle.bg.replace("bg-", "border-").replace("-50", "-200")} px-3 py-2`}>
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Verificado manualmente
            </span>
            <span className={`inline-block w-2 h-2 rounded-full ${verifiedStyle.dot}`} />
          </div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <span className={`text-xl font-bold ${verifiedStyle.text}`} data-testid="verified-pct">
              {summary.verified_pct}%
            </span>
            <span className="text-[11px] text-muted-foreground">
              ({summary.verified}/{summary.total_dishes})
            </span>
          </div>
        </div>

        <div className="rounded-lg bg-card/80 border border-border px-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Última verificación
            </span>
            <FileText className="w-3 h-3 text-slate-400" />
          </div>
          <div className="mt-1">
            <span className="text-sm font-semibold text-foreground">
              {summary.last_verified_at
                ? new Date(summary.last_verified_at).toLocaleDateString("es-ES", {
                    day: "2-digit", month: "short", year: "numeric",
                  })
                : "—"}
            </span>
          </div>
        </div>
      </div>

      {/* Hint */}
      {overall !== "green" && (
        <div className="flex items-start gap-2 text-[11px] text-muted-foreground bg-card/60 rounded-lg px-3 py-2 border border-border">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-muted-foreground" />
          <p>
            Objetivo óptimo: <strong>≥90%</strong> de cobertura y verificación.
            {overall === "red" && " Uno o más KPIs están por debajo del 70% — prioriza revisar los platos pendientes antes de una inspección."}
            {overall === "yellow" && " Estás cerca del objetivo — completa la verificación manual de los platos restantes."}
          </p>
        </div>
      )}
    </div>
  );
}

export default ComplianceCard;
